import {
  STREAMLAIN_AI_ISLAND_INSET,
  STREAMLAIN_AI_ISLAND_SIZE,
} from '@blocksuite/affine-shared/styles';

import { KEYBOARD_INSET_PROPERTY } from './keyboard-inset';

export {
  AI_ISLAND_AVOID_INLINE_END_PROPERTY,
  STREAMLAIN_AI_ISLAND_DEFAULT_SNAP_POINT,
  STREAMLAIN_AI_ISLAND_INSET,
  STREAMLAIN_AI_ISLAND_SIZE,
  STREAMLAIN_AI_ISLAND_SNAP_POINTS,
  STREAMLAIN_APP_TOPBAR_HEIGHT,
  type StreamlainAIIslandSnapPoint,
} from '@blocksuite/affine-shared/styles';

export const STREAMLAIN_TWO_PANE_OVERLAY_QUERY = '(max-width: 960px)';
export const STREAMLAIN_THREE_PANE_COMPACT_QUERY = '(max-width: 820px)';

/**
 * The AI island's bottom above the active view's bottom: its inset, raised over an open on-screen
 * keyboard. Views put their bottom bars above the island (WorkspacePaneBottomBar), never under it.
 */
export const STREAMLAIN_AI_ISLAND_BOTTOM = `calc(${STREAMLAIN_AI_ISLAND_INSET}px + var(${KEYBOARD_INSET_PROPERTY}, 0px))`;

/** The island's size while the copilot docks to it: as large as the input's send button. */
export const STREAMLAIN_AI_ISLAND_DOCKED_SIZE = 32;

/** Corner radius shared by the AI surfaces: the copilot panel and the context dock look alike. */
export const STREAMLAIN_AI_SURFACE_RADIUS = 12;
/** Theme token of the AI surfaces' background, shared by the copilot panel and the context dock. */
export const STREAMLAIN_AI_SURFACE_BACKGROUND = 'layer/background/appChrome';

/** The context dock's row: the island centred in it, the view's tools beside it. */
export const STREAMLAIN_CONTEXT_DOCK_HEIGHT = 64;
/** The dock row's padding around the island (and its tools). */
export const STREAMLAIN_CONTEXT_DOCK_PADDING =
  (STREAMLAIN_CONTEXT_DOCK_HEIGHT - STREAMLAIN_AI_ISLAND_SIZE) / 2;
/** The dock row's bottom: the island keeps its place whether or not tools show beside it. */
export const STREAMLAIN_CONTEXT_DOCK_BOTTOM = `calc(${STREAMLAIN_AI_ISLAND_BOTTOM} - ${STREAMLAIN_CONTEXT_DOCK_PADDING}px)`;

/** The widest phone-sized window or view, px. */
export const STREAMLAIN_PHONE_WIDTH = 540;
/** A phone: the navigation rail opens from the top bar's burger. */
export const STREAMLAIN_PHONE_QUERY = `(max-width: ${STREAMLAIN_PHONE_WIDTH}px)`;
/** The narrowest a view gets beside the sidebar or another view of a split, px. */
export const STREAMLAIN_VIEW_MIN_WIDTH = 340;
/** A phone's dock row on the view's bottom, the island in its middle. */
export const STREAMLAIN_PHONE_DOCK_HEIGHT = 48;
/** The phone row's bottom: above the home indicator and an open on-screen keyboard. */
export const STREAMLAIN_PHONE_DOCK_BOTTOM = `calc(env(safe-area-inset-bottom, 0px) + var(${KEYBOARD_INSET_PROPERTY}, 0px))`;

/**
 * Body property: the room a scroll area keeps free at its end so its last content scrolls clear of
 * the dock; less beside the naked island. Scroll areas take it with `SCROLL_AIR_ATTRIBUTE`.
 */
export const DOCK_SCROLL_AIR_PROPERTY = '--streamlain-dock-scroll-air';
/** Marks a view's scroll area that keeps the dock's room free at its end. */
export const SCROLL_AIR_ATTRIBUTE = 'data-streamlain-scroll-air';

/** The floating AI panel's inset from the view's top edge and the gap between its history and its input. */
export const STREAMLAIN_AI_PANEL_INSET = 12;
/** A phone-sized view's AI panel: its inset from the view's sides. */
export const STREAMLAIN_AI_PANEL_PHONE_INSET = 8;
/** Above a phone panel a strip of the view stays visible, so a tap there puts the history away. */
export const STREAMLAIN_AI_PANEL_PHONE_VIEW_PEEK = 120;
/** A phone panel's height until the user resizes it. */
export const STREAMLAIN_AI_PANEL_PHONE_HEIGHT = '50%';
/** The AI panel's width until the user resizes it. */
export const STREAMLAIN_AI_PANEL_WIDTH = 520;
/** The AI panel's width while it previews a result. */
export const STREAMLAIN_AI_PANEL_PREVIEW_WIDTH = 1080;
/** The AI panel's height until the user resizes it. */
export const STREAMLAIN_AI_PANEL_HEIGHT = '40vh';
/** The smallest size the user can resize the AI panel to. */
export const STREAMLAIN_AI_PANEL_MIN_WIDTH = 320;
export const STREAMLAIN_AI_PANEL_MIN_HEIGHT = 240;
